import React, { useCallback, useMemo, useRef, useState } from 'react';
import type { BulkFinalValueMode } from './user-params-grid';
import { Card, Row } from 'react-bootstrap';
import { useMutation, useQuery } from '@apollo/client';
import { Field } from 'formik';
import {
  CustomSelectComponent,
  FieldInput,
  FlexRow,
  FormCol,
  FormFilterCard,
  promiseModalDialog,
  toastFailure,
  toastSuccess,
} from 'motion-components';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { userTypeIdToEntityType } from 'shared-pages/common/entity-params-types';
import {
  gqlAddUsrParamsValue,
  gqlDeleteUsrParamsValue,
  gqlGetAppparams,
  gqlGetEntityParams,
  gqlGetUserUsrParamsValues,
  gqlUpdateUsrParamsValue,
} from './graphql';
import { DataGrid } from './user-params-grid';

interface Props {
  userName: string;
  userTypeId: number;
}

interface ResolvedRow {
  paramName: string;
  paramGroup: string;
  description: string;
  notes: string;
  defaultValue: boolean;
  finalValue: boolean;
  hasOverride: boolean;
  overrideId?: number;
}

const EMPTY_LIST: any[] = [];

function isAppliesEnabled(value: any): boolean {
  return !(value === false || value === 0 || value === '0');
}

function paramIntToBool(value: any): boolean {
  return value === true || value === 1 || value === '1';
}

/** True if bulk apply would change persisted state for this row. */
function rowNeedsBulkUpdate(row: ResolvedRow, mode: BulkFinalValueMode): boolean {
  const currentOn = !!row.finalValue;
  if (mode === 'default') {
    return row.hasOverride;
  }
  const targetOn = mode === 'yes';
  if (currentOn === targetOn) {
    return false;
  }
  const targetVal = targetOn ? 1 : 0;
  const defaultVal = row.defaultValue ? 1 : 0;
  if (targetVal === defaultVal) {
    return row.hasOverride;
  }
  return true;
}

function filterVisibleRows(
  rows: ResolvedRow[],
  filterValues: { searchText: string; paramGroup: string },
): ResolvedRow[] {
  const search = (filterValues.searchText ?? '').trim().toLowerCase();
  const group = filterValues.paramGroup ?? '';
  return rows.filter((row) => {
    if (
      search &&
      !String(row.paramName ?? '')
        .toLowerCase()
        .includes(search)
    )
      return false;
    if (group && row.paramGroup !== group) return false;
    return true;
  });
}

/* ----------------------------------------------------------- */
/* ------------ Παράμετροι Χρήστη (resolved 2-level) ---------- */
export const UserParamsSection: React.FC<Props> = observer(({ userName, userTypeId }) => {
  const { t } = useTranslation();
  const { appStore } = useRootStore();
  const gridRef = useRef();
  const [filterValues, setFilterValues] = useState({
    searchText: '',
    paramGroup: '',
  });
  const [bulkSaving, setBulkSaving] = useState(false);

  const entityType = userTypeIdToEntityType(userTypeId);

  console.log('appStore.user', appStore.user);
  console.log('userName', userName);
  const canEdit = appStore.user === userName || appStore.isPlatformOwner;

  /* -------- Defaults από entity_params (φιλτραρισμένα) ---------- */
  const { data: entityData, loading: entityLoading } = useQuery(gqlGetEntityParams, {
    variables: {
      where: entityType ? JSON.stringify({ entityType }) : JSON.stringify({}),
      limit: 1000,
      offset: 0,
      order: 'paramName ASC',
    },
    skip: !entityType,
    fetchPolicy: 'cache-and-network',
  });

  const { data: appparamsData, loading: appparamsLoading } = useQuery(gqlGetAppparams, {
    variables: {
      where: JSON.stringify({}),
      limit: 1000,
      offset: 0,
      order: 'paramName ASC',
    },
    fetchPolicy: 'cache-and-network',
  });

  /* -------- Overrides από usr_params_values --------------------- */
  const {
    data: overridesData,
    loading: overridesLoading,
    refetch: refetchOverrides,
  } = useQuery(gqlGetUserUsrParamsValues, {
    variables: { userName },
    skip: !userName,
    fetchPolicy: 'cache-and-network',
  });

  const [addOverride] = useMutation(gqlAddUsrParamsValue);
  const [updateOverride] = useMutation(gqlUpdateUsrParamsValue);
  const [deleteOverride] = useMutation(gqlDeleteUsrParamsValue);

  const defaults = entityData?.getEntityParams?.data ?? EMPTY_LIST;
  const appparams = appparamsData?.getAppparams?.data ?? EMPTY_LIST;
  const overrides = overridesData?.getUserUsrParamsValues?.data ?? EMPTY_LIST;

  /* -------- Client-side merge ----------------------------------- */
  const rows: ResolvedRow[] = useMemo(() => {
    const appParamMap = new Map<string, any>();
    appparams.forEach((a) => appParamMap.set(a.paramName, a));
    const overrideMap = new Map<string, any>();
    overrides.forEach((o) => overrideMap.set(o.paramName, o));

    return defaults
      .filter((d) => isAppliesEnabled(d?.applies))
      .map((d) => {
        const ov = overrideMap.get(d.paramName);
        const app = appParamMap.get(d.paramName);
        const defaultValue = paramIntToBool(d.intValue);
        const hasOverride = !!ov;
        const finalValue = hasOverride ? paramIntToBool(ov.intValue) : defaultValue;
        return {
          paramName: d.paramName,
          paramGroup: app?.paramGroup ?? '',
          description: app?.description ?? '',
          notes: app?.notes ?? '',
          defaultValue,
          finalValue,
          hasOverride,
          overrideId: ov?.id,
        };
      });
  }, [defaults, appparams, overrides]);

  const groupOptions = useMemo(() => {
    const groups = Array.from(
      new Set(
        appparams.map((a) => a?.paramGroup).filter((g) => !!g && String(g).trim().length > 0),
      ),
    ).sort((a: any, b: any) => String(a).localeCompare(String(b)));
    return [
      { value: '', label: t('Όλες οι Ομάδες') },
      ...groups.map((g) => ({ value: g, label: g })),
    ];
  }, [appparams, t]);

  const filteredRows: ResolvedRow[] = useMemo(
    () => filterVisibleRows(rows, filterValues),
    [rows, filterValues],
  );

  // TablePaginatedV8 freezes columns on first mount — bulk handler must read latest rows via ref.
  const bulkSourceRef = useRef({ rows: [] as ResolvedRow[], filterValues });
  bulkSourceRef.current = { rows, filterValues };

  const handleFiltersSubmit = useCallback((values: any, { setSubmitting }: any) => {
    setTimeout(() => {
      const nextFilterValues = {
        searchText: values?.searchText ?? '',
        paramGroup: values?.paramGroup ?? '',
      };
      setSubmitting(false);
      setFilterValues(nextFilterValues);
    }, 200);
  }, []);

  /* -------- apply finalValue (single row) ----------------------- */
  const applyFinalValue = useCallback(
    async (row: ResolvedRow, nextValue: boolean): Promise<boolean> => {
      const newVal = nextValue ? 1 : 0;
      const defaultInt = row.defaultValue ? 1 : 0;

      if (newVal === defaultInt) {
        if (row.hasOverride && row.overrideId) {
          const res = await deleteOverride({ variables: { id: row.overrideId } }).catch(
            (reason) => {
              console.error('user-params delete override error', reason);
            },
          );
          return !!res;
        }
        return true;
      }

      if (row.hasOverride && row.overrideId) {
        const res = await updateOverride({
          variables: { data: { id: row.overrideId, intValue: newVal } },
        }).catch((reason) => {
          console.error('user-params update override error', reason);
        });
        return !!res;
      }

      const res = await addOverride({
        variables: { data: { userName, paramName: row.paramName, intValue: newVal } },
      }).catch((reason) => {
        console.error('user-params add override error', reason);
      });
      return !!res;
    },
    [addOverride, deleteOverride, updateOverride, userName],
  );

  const handleSaveRow = useCallback(
    async (row: ResolvedRow, changes: any) => {
      if (!canEdit) return false;
      const hasFinalValue = 'finalValue' in changes;
      const hasEffectiveValue = 'effectiveValue' in changes;
      if (!hasFinalValue && !hasEffectiveValue) return row.paramName;

      const nextValue = hasFinalValue ? changes.finalValue : changes.effectiveValue;
      const ok = await applyFinalValue(row, nextValue);
      if (!ok) {
        toastFailure(t('Η αποθήκευση απέτυχε'));
        return false;
      }
      await refetchOverrides();
      const matchesDefault = (nextValue ? 1 : 0) === (row.defaultValue ? 1 : 0);
      toastSuccess(
        matchesDefault && row.hasOverride ? t('Επαναφέρθηκε στις προεπιλογές') : t('Αποθηκεύτηκε'),
      );
      return row.paramName;
    },
    [applyFinalValue, canEdit, refetchOverrides, t],
  );

  const handleBulkFinalValue = useCallback(
    async (mode: BulkFinalValueMode) => {
      if (!canEdit) return;

      const { rows: sourceRows, filterValues: fv } = bulkSourceRef.current;
      const visibleRows = filterVisibleRows(sourceRows, fv);
      const rowsToChange = visibleRows.filter((row) => rowNeedsBulkUpdate(row, mode));

      if (rowsToChange.length === 0) {
        toastSuccess(t('Δεν απαιτείται αλλαγή'));
        return;
      }

      const actionText =
        mode === 'yes'
          ? t('Όλα → ΝΑΙ')
          : mode === 'no'
          ? t('Όλα → ΟΧΙ')
          : t('Επαναφορά στις προεπιλογές');

      const confirmed = await promiseModalDialog({
        title: t('Μαζική αλλαγή'),
        text: `<div>${t('Ενέργεια')}: <strong>${actionText}</strong><br/>${t(
          'Παράμετροι προς ενημέρωση',
        )}: <strong>${rowsToChange.length}</strong> ${t('(ορατές μετά το φίλτρο)')}</div>`,
      });
      if (!confirmed) return;

      setBulkSaving(true);
      try {
        let failed = 0;
        for (const row of rowsToChange) {
          const nextValue = mode === 'default' ? row.defaultValue : mode === 'yes';
          const ok = await applyFinalValue(row, nextValue);
          if (!ok) failed += 1;
        }

        await refetchOverrides();

        if (failed > 0) {
          toastFailure(
            t('Ολοκληρώθηκε με {{failed}} αποτυχίες από {{total}}', {
              failed,
              total: rowsToChange.length,
            }),
          );
          return;
        }

        toastSuccess(t('Ενημερώθηκαν {{count}} παράμετροι', { count: rowsToChange.length }));
      } finally {
        setBulkSaving(false);
      }
    },
    [applyFinalValue, canEdit, refetchOverrides, t],
  );

  /* -------- Render --------------------------------------------- */
  if (!entityType) {
    return (
      <Card className="shadow mt-3">
        <Card.Header>
          <FlexRow>
            <Card.Title className="m-0">{t('Παράμετροι Χρήστη')}</Card.Title>
          </FlexRow>
        </Card.Header>
        <Card.Body>
          <div className="text-muted">
            {t('Δεν υπάρχουν διαθέσιμες παράμετροι για αυτόν τον τύπο χρήστη.')}
          </div>
        </Card.Body>
      </Card>
    );
  }

  const loading = entityLoading || appparamsLoading || overridesLoading;
  const gridLoading = loading || bulkSaving;

  return (
    <Card className="shadow mt-3">
      <Card.Header>
        <FlexRow>
          <Card.Title className="m-0">{t('Παράμετροι Χρήστη')}</Card.Title>
          {!canEdit && <small className="text-muted">{t('(μόνο για ανάγνωση)')}</small>}
        </FlexRow>
      </Card.Header>
      <Card.Body className="nopadding">
        <FormFilterCard
          title={t('Κριτήρια Αναζήτησης')}
          onSubmit={handleFiltersSubmit}
          initialValues={filterValues}
        >
          <Row>
            <FieldInput
              as={FormCol}
              name="searchText"
              label={t('Αναζήτηση (Παράμετρος)')}
              type="search"
            />
            <Field
              as={FormCol}
              name="paramGroup"
              title={t('Ομάδα Παραμέτρων')}
              component={CustomSelectComponent}
              options={groupOptions}
            />
          </Row>
        </FormFilterCard>
        <DataGrid
          ref={gridRef}
          loading={gridLoading}
          data={filteredRows}
          canEdit={canEdit}
          onSaveRow={handleSaveRow}
          onBulkFinalValue={handleBulkFinalValue}
        />
      </Card.Body>
    </Card>
  );
});
